// Per-bot parameters (§10.5, §10.6): the difficulty row and the personality row of TUNE.bot merged
// into one flat record, plus a small seeded per-bot variation so two bots of the same personality
// and difficulty never move in lockstep. Gary (§10.5) is a Chaos Goblin with his own quirks.
import { TUNE } from '../constants';
import { Difficulty, Personality } from '../ids';
import { DEG } from '../math';
import { randRange, type RngHolder } from '../rng';
import type { BotConfig } from './botTypes';
import { BOT_TUNE } from './tuning';

export interface BotParams {
  difficulty: Difficulty;
  personality: Personality;
  gary: boolean;
  // ---- difficulty (§10.6) ----
  /** Base reaction delay in ticks, before the room's adaptive multiplier. */
  reactionTicks: number;
  /** Aim noise standard deviation, radians. */
  aimNoise: number;
  /** Edge margin in meters, already multiplied by the personality's edgeMarginMult. */
  edgeMargin: number;
  /** Blunder chance per lunge, already multiplied by the personality's blunderMult. */
  blunderChance: number;
  /** Ticks between escape inputs while GRABBED. */
  mashInterval: number;
  teeterSave: number;
  ledgeMindGame: number;
  /** Hazard timing error standard deviation, ticks. */
  hazardSigma: number;
  jumpDodge: number;
  /** Clash attempt probability, already multiplied by the personality's clashMult. */
  clash: number;
  di: number;
  /** Fraction of a target's velocity lead used for bombs and plungers (0, 0.5, 1). */
  lead: number;
  /** Chance per think to read a coming shove before it happens. */
  anticipate: number;
  /** Stick low-pass factor per tick (1 = no smoothing). */
  stickSmoothing: number;
  /** Ticks the bot hesitates before committing a lined-up shove (max of a uniform draw). */
  commitJitter: number;
  /** Chance to bait a ready, facing opponent instead of walking into its lunge. */
  baitChance: number;
  /** How seriously hazard danger is taken (Chill bots get caught out by telegraphs more). */
  hazardRespect: number;
  /** Hover ticks before committing without an opening (uniform in [min, max]). */
  patienceMin: number;
  patienceMax: number;
  /** Range misjudgment σ per approach, meters. */
  rangeErr: number;
  /** Chance (rolled once a second) to take a point-blank shove at the rim that will teeter. */
  edgeGreed: number;
  // ---- personality (§10.5) ----
  aggression: number;
  grabPref: number;
  itemGreed: number;
  honksPerMin: number;
  leaderFocus: number;
  exposureWeight: number;
  minExposure: number;
  shoveAnyoneRange: number;
  align: number;
  prefersStrongest: boolean;
  ledgeMindGames: boolean;
  centerHugger: boolean;
  rangedItems: boolean;
  crowdBombs: boolean;
  /** WANDER standoff from the action centroid, meters. */
  standoff: number;
}

/** Seeded ±spread variation around 1. */
function vary(rng: RngHolder, spread: number): number {
  return randRange(rng, 1 - spread, 1 + spread);
}

/**
 * Resolves the parameters of one bot. Gary always plays as a Chaos Goblin. Draws from `rng`
 * (the brain's own stream), so the variation is part of the bot's seeded identity.
 */
export function resolveParams(cfg: BotConfig, rng: RngHolder): BotParams {
  const gary = cfg.isGary === true;
  const personality = gary ? Personality.CHAOS : cfg.personality;
  const difficulty = cfg.difficulty;
  const d = TUNE.bot.difficulty[difficulty] ?? TUNE.bot.difficulty[Difficulty.NORMAL];
  const p = TUNE.bot.personality[personality] ?? TUNE.bot.personality[Personality.BULLY];
  const feel = BOT_TUNE.feel;
  const spread = feel.individualSpread;
  const g = BOT_TUNE.gary;
  return {
    difficulty, personality, gary,
    reactionTicks: d.reactionTicks * vary(rng, spread),
    aimNoise: d.aimNoiseDeg * DEG,
    edgeMargin: d.edgeMargin * p.edgeMarginMult,
    blunderChance: d.blunderChance * p.blunderMult * (gary ? g.blunderMult : 1),
    mashInterval: 60 / d.mashRate,
    teeterSave: d.teeterSaveChance,
    ledgeMindGame: p.ledgeMindGames ? Math.min(1, d.ledgeMindGameChance + feel.tacticianMindGameBonus) : d.ledgeMindGameChance,
    hazardSigma: d.hazardTimingSigmaTicks,
    jumpDodge: d.jumpDodgeChance,
    clash: Math.min(1, d.clashChance * p.clashMult),
    di: d.diChance,
    lead: d.leadFraction,
    anticipate: feel.anticipateChance[difficulty],
    stickSmoothing: feel.stickSmoothing[difficulty],
    commitJitter: feel.commitJitterTicks[difficulty],
    baitChance: feel.baitChance[difficulty] * (p.ledgeMindGames ? feel.tacticianBaitMult : 1),
    patienceMin: feel.patienceMin[difficulty] * (feel.patienceMult[personality] ?? 1) * vary(rng, spread),
    patienceMax: feel.patienceMax[difficulty] * (feel.patienceMult[personality] ?? 1) * vary(rng, spread),
    rangeErr: feel.rangeErrSigma[difficulty],
    edgeGreed: Math.min(1, feel.edgeGreed[difficulty] * p.blunderMult),
    hazardRespect: feel.hazardRespect[difficulty],
    aggression: p.aggression * vary(rng, spread),
    grabPref: p.grabPref,
    itemGreed: p.itemGreed,
    honksPerMin: gary ? g.honksPerMin : p.honksPerMin,
    leaderFocus: p.leaderFocus,
    exposureWeight: p.exposureWeight,
    minExposure: p.minExposure,
    shoveAnyoneRange: p.shoveAnyoneRange,
    align: p.align,
    prefersStrongest: p.prefersStrongest,
    ledgeMindGames: p.ledgeMindGames,
    centerHugger: p.centerHugger,
    rangedItems: p.rangedItems,
    crowdBombs: p.crowdBombs,
    standoff: p.centerHugger ? TUNE.bot.wander.cowardStandoff : TUNE.bot.wander.standoff,
  };
}
